import { Button, Modal } from "@nabble/ui";
import { useState } from "react";
import type { AssignableRole, OrganizationMember } from "../../../api/organizations";
import { RolePicker } from "../../../components/RolePicker";

interface EditMemberRolesDialogProps {
  member: OrganizationMember;
  roles: AssignableRole[];
  onClose: () => void;
  onSave: (roleIds: string[]) => Promise<void>;
}

/** Chooses the roles a member holds in the organization; they get every permission of all of them. */
export function EditMemberRolesDialog({ member, roles, onClose, onSave }: EditMemberRolesDialogProps): React.JSX.Element {
  const [roleIds, setRoleIds] = useState<string[]>(member.roles.map((r) => r.id));
  const [saving, setSaving] = useState(false);

  async function save(): Promise<void> {
    setSaving(true);
    try {
      await onSave(roleIds);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      icon="key"
      title={`Roles of ${member.displayName}`}
      description="A person can hold several roles. They get every permission of all of them in this organization."
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button label="Save roles" loading={saving} disabled={roleIds.length === 0} onClick={() => void save()} />
        </>
      }
    >
      <RolePicker roles={roles} value={roleIds} onChange={setRoleIds} label={`Roles of ${member.displayName}`} />
    </Modal>
  );
}
